import React, { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, Clock, Send, MessageCircle, HelpCircle, CheckCircle } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import { inquiryService } from '../lib/database';
import toast from 'react-hot-toast';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const validateForm = () => {
    if (!formData.name.trim()) {
      toast.error('Please enter your name');
      return false;
    }
    
    if (!formData.email.trim()) {
      toast.error('Please enter your email');
      return false;
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email.trim())) {
      toast.error('Please enter a valid email address');
      return false;
    }
    
    if (!formData.subject.trim()) {
      toast.error('Please select a subject');
      return false;
    }
    
    if (!formData.message.trim()) {
      toast.error('Please enter your message');
      return false;
    }

    if (formData.message.trim().length < 10) {
      toast.error('Please enter a more detailed message (at least 10 characters)');
      return false;
    }

    return true;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    
    try {
      await inquiryService.create({
        name: formData.name.trim(),
        email: formData.email.trim(),
        subject: formData.subject.trim(),
        message: formData.message.trim()
      });

      toast.success('🎉 Thank you for your message! We\'ll get back to you within 24 hours.');
      
      // Reset form and show success state
      setFormData({ name: '', email: '', subject: '', message: '' });
      setSubmitted(true);
      
      // Reset submitted state after 5 seconds
      setTimeout(() => setSubmitted(false), 5000);
      
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error('❌ Failed to send message. Please try again or contact us directly.');
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: Mail,
      title: 'Email Us',
      details: 'vikalpgrad@gmail.com',
      description: 'Send us an email anytime',
      color: 'text-primary',
      bgColor: 'bg-primary'
    },
    {
      icon: Phone,
      title: 'Call Us',
      details: '+91 7568377278',
      description: 'Mon-Fri from 9am to 6pm',
      color: 'text-success',
      bgColor: 'bg-success'
    },
    {
      icon: MapPin,
      title: 'Visit Us',
      details: 'Jaipur, India',
      description: 'Come say hello at our office',
      color: 'text-info',
      bgColor: 'bg-info'
    },
    {
      icon: Clock,
      title: 'Working Hours',
      details: '9:00 AM - 6:00 PM',
      description: 'Monday to Friday',
      color: 'text-warning',
      bgColor: 'bg-warning'
    }
  ];

  const faqs = [
    {
      question: 'How does the personalized college list work?',
      answer: 'Our AI algorithm analyzes your academic profile, preferences, and goals to generate a customized list of colleges that match your criteria.'
    },
    {
      question: 'Are the college reviews verified?',
      answer: 'Yes, all reviews on our platform are verified from real students and alumni. We have a strict verification process to ensure authenticity.'
    },
    {
      question: 'Is the service free to use?',
      answer: 'Basic features like college search and reviews are free. Premium features like detailed counseling sessions may have associated costs.'
    },
    {
      question: 'How can I connect with current students?',
      answer: 'You can use our student connect feature to chat with current students and alumni from colleges you\'re interested in.'
    }
  ];

  return (
    <div style={{ paddingTop: '80px' }} className="bg-light">
      <SEOHead
        title="Contact Grad Vikalp - Get College Guidance & Support"
        description="Contact Grad Vikalp for college guidance, admission counseling, and support. Reach out via email, phone, or our contact form. We're here to help with your college selection journey."
        keywords="contact grad vikalp, college guidance support, admission counseling, education help, college selection assistance, student support"
        url="/contact"
        type="website"
        pageKey="contact"
      />
      <div className="container py-5">
        {/* Header */}
        <div className="text-center mb-5">
          <h1 className="display-4 fw-bold text-dark mb-3">Get in Touch</h1>
          <p className="lead text-muted mx-auto" style={{ maxWidth: '600px' }}>
            Have questions about college selection? We're here to help you every step of the way.
          </p>
        </div>

        <div className="row g-4 g-lg-5">
          {/* Contact Form */}
          <div className="col-lg-8">
            <div className="card border-0 shadow-lg">
              <div className="card-body p-4 p-md-5">
                <h2 className="h3 fw-bold text-dark mb-4">Send us a Message</h2>
                
                {submitted ? (
                  <div className="text-center py-5">
                    <div className="mb-4">
                      <CheckCircle size={64} className="text-success" />
                    </div>
                    <h3 className="h4 text-success mb-3">Message Sent Successfully!</h3>
                    <p className="text-muted mb-4">
                      Thank you for contacting us. We'll get back to you within 24 hours.
                    </p>
                    <button 
                      onClick={() => setSubmitted(false)}
                      className="btn btn-primary"
                    >
                      Send Another Message
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} noValidate className="contact-form">
                    <div className="row g-3 mb-3">
                      <div className="col-12 col-md-6">
                        <label className="form-label fw-semibold text-dark">
                          Full Name *
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          disabled={loading}
                          className="form-control form-control-lg border-2"
                          placeholder="Enter your full name"
                          autoComplete="name"
                          style={{ 
                            fontSize: '16px', // Prevents zoom on iOS
                            minHeight: '50px'
                          }}
                        />
                      </div>
                      
                      <div className="col-12 col-md-6">
                        <label className="form-label fw-semibold text-dark">
                          Email Address *
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          disabled={loading}
                          className="form-control form-control-lg border-2"
                          placeholder="Enter your email"
                          autoComplete="email"
                          style={{ 
                            fontSize: '16px', // Prevents zoom on iOS
                            minHeight: '50px'
                          }}
                        />
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label fw-semibold text-dark">
                        Subject *
                      </label>
                      <select
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        required
                        disabled={loading}
                        className="form-select form-select-lg border-2"
                        style={{ 
                          fontSize: '16px', // Prevents zoom on iOS
                          minHeight: '50px'
                        }}
                      >
                        <option value="">Select a subject</option>
                        <option value="College Guidance">College Guidance</option>
                        <option value="Technical Support">Technical Support</option>
                        <option value="Partnership Inquiry">Partnership Inquiry</option>
                        <option value="Feedback">Feedback</option>
                        <option value="General Inquiry">General Inquiry</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    <div className="mb-4">
                      <label className="form-label fw-semibold text-dark">
                        Message *
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        disabled={loading}
                        rows={6}
                        className="form-control form-control-lg border-2"
                        placeholder="Tell us how we can help you..."
                        style={{ 
                          fontSize: '16px', // Prevents zoom on iOS
                          resize: 'vertical',
                          minHeight: '120px'
                        }}
                      ></textarea>
                      <div className="form-text">
                        {formData.message.length}/500 characters
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="btn btn-primary btn-lg w-100 d-flex align-items-center justify-content-center gap-2 shadow-sm"
                      style={{ minHeight: '50px' }}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                          <span>Sending...</span>
                        </>
                      ) : (
                        <>
                          <Send size={20} />
                          <span>Send Message</span>
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>

          {/* Contact Info */}
          <div className="col-lg-4">
            <div className="vstack gap-4">
              {/* Contact Details */}
              <div className="card border-0 shadow-lg">
                <div className="card-body p-4">
                  <h3 className="h5 fw-bold text-dark mb-4 text-center text-lg-start">Contact Information</h3>
                  <div className="vstack gap-4">
                    {contactInfo.map((info, index) => {
                      const Icon = info.icon;
                      return (
                        <div key={index} className="d-flex align-items-center gap-3 text-center text-lg-start">
                          <div className="flex-shrink-0">
                            <div className={`feature-icon ${info.bgColor} bg-opacity-10 d-flex align-items-center justify-content-center`}>
                              <Icon className={info.color} size={20} />
                            </div>
                          </div>
                          <div className="flex-grow-1">
                            <h4 className="h6 fw-semibold text-dark mb-1">{info.title}</h4>
                            <p className="text-dark mb-1 fw-medium">{info.details}</p>
                            <p className="text-muted small mb-0">{info.description}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="card border-0 shadow-lg">
                <div className="card-body p-4">
                  <h3 className="h5 fw-bold text-dark mb-4 text-center text-lg-start">Quick Actions</h3>
                  <div className="vstack gap-3">
                    <button className="btn btn-primary btn-lg w-100 d-flex align-items-center justify-content-center gap-3">
                      <MessageCircle size={20} />
                      <span>Start Live Chat</span>
                    </button>
                    <a 
                      href="tel:+917568377278"
                      className="btn btn-success btn-lg w-100 d-flex align-items-center justify-content-center gap-3 text-decoration-none"
                    >
                      <Phone size={20} />
                      <span>Call Now</span>
                    </a>
                    <button className="btn btn-info btn-lg w-100 d-flex align-items-center justify-content-center gap-3">
                      <HelpCircle size={20} />
                      <span>View FAQ</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-5">
          <div className="text-center mb-5">
            <h2 className="h1 fw-bold text-dark mb-3">Frequently Asked Questions</h2>
            <p className="text-muted">Quick answers to common questions</p>
          </div>

          <div className="row g-4">
            {faqs.map((faq, index) => (
              <div key={index} className="col-md-6">
                <div className="card border-0 shadow-lg h-100">
                  <div className="card-body p-4">
                    <h3 className="h5 fw-bold text-dark mb-3">{faq.question}</h3>
                    <p className="text-muted mb-0">{faq.answer}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-5">
          <div className="card bg-blue-gradient border-0 text-white">
            <div className="card-body p-4 p-md-5 text-center">
              <h3 className="h2 fw-bold mb-3">
                Still have questions?
              </h3>
              <p className="text-blue-100 mb-4">
                Our team of education experts is here to help you make the right college choice.
              </p>
              <button className="btn btn-light text-primary btn-lg fw-semibold">
                Book a Free Consultation
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;